/* eslint-disable functional/immutable-data */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable max-lines-per-function */
import {
  Card,
  CardHeader,
  Collapse,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import React, { useState } from 'react'
import InternalContent from '~/components/Layout/components/InternalContent'
import { useLang } from '~/hooks/useLang'
import { useHookstate } from '@hookstate/core'
import {
  DataGrid,
  type GridColDef,
  type GridRowsProp,
  type GridSortModel,
  gridClasses,
} from '@mui/x-data-grid'
import { ptBR } from '@mui/x-data-grid/locales'
import dayjs from 'dayjs'
import notify from '~/utils/notify'
import {
  getDeviceLogDetails,
} from '../services'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AuditDeviceReportFilters from './AuditDeviceReportFilters'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AuditLogDetailsModal from '../../AuditLogDetailsModal'
import getModalFields from './getModalFields'

import { DeviceReportSearchLogRow } from '../interfaces'
import { fetchDeviceReportLogs, mapSearchLogDataToRows, validateSearchParams } from '../utils'
import { findDeviceLabelByUrl } from './reportTypeOptions'

export default function ListAllAuditDeviceReport() {
  const { translate } = useLang()
  const labelButton = useHookstate(translate('Search'))
  const action = useHookstate<string | null>(null)
  const loadingButton = useHookstate<boolean>(false)
  const expandFilterSearch = useHookstate<boolean>(true)
  const userId = useHookstate<number | null>(null)
  const resetValues = useHookstate(false)
  const focusChange = useHookstate<boolean>(false)
  const initialPeriod = useHookstate<string>('')
  const originType = useHookstate<'device' | 'station' | null>(null)
  const selectedDevice = useHookstate<number | null>(null)
  const selectedStations = useHookstate<number | null>(null)
  const [startDate, setStartDate] = useState<string | null>(null)
  const [finishDate, setFinishDate] = useState<string | null>(null)
  const limit = 15
  const [rows, setRows] = React.useState<GridRowsProp>([])
  const [rowCount, setRowCount] = React.useState(0)
  const [paginationModel, setPaginationModel] = React.useState<{
    readonly page: number
    readonly pageSize: number
  }>({ page: 0, pageSize: limit })
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])
  const tableMode = 'server'
  const expandDeviceReports = useHookstate<boolean>(false)
  const [openModal, setOpenModal] = useState(false)
  const [selectedRow, setSelectedRow] =
    useState<DeviceReportSearchLogRow | null>(null)
  const [loadingRowId, setLoadingRowId] = useState<number | null>(null) // Novo state para loading do row específico
  const [openTooltips, setOpenTooltips] = useState<Record<number, boolean>>({})

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setOpenModal(false)
    }
  }

  // usa o array de options para criar o mapa '/rota': 'Label traduzida'
  const getReportNameFromRoute = (route?: string): string => {
    if (!route) return translate('Unknown Report')
    return findDeviceLabelByUrl(route, translate) ?? route
  }

  // Função para extrair datas do content
  const extractDatesFromContent = (content: string) => {
    try {
      const parsedContent = JSON.parse(content)
      return {
        start_date: parsedContent.start_date || null,
        finish_date: parsedContent.finish_date || null,
      }
    } catch {
      return {
        start_date: null,
        finish_date: null,
      }
    }
  }
  const columns: readonly GridColDef[] = [
    {
      field: 'user:representation_name',
      headerName: translate('User'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'action_display',
      headerName: translate('Action'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      valueGetter: (value, row) => row.action_display,
    },
    {
      field: 'origin',
      headerName: translate('Origin'),
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'created_at',
      headerName: translate('Performed at'),
      type: 'date',
      flex: 1,
      filterable: false,
      sortable: true,
      disableColumnMenu: false,
      valueFormatter: (value) =>
        dayjs(value as string).format('DD/MM/YYYY HH:mm'),
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      align: 'center',
      width: 80,
      renderCell: (params) => (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Tooltip
            title={translate('Visualizar')}
            open={openTooltips[params.row.id] ?? false}
            onOpen={() => setOpenTooltips(prev => ({ ...prev, [params.row.id]: true }))}
            onClose={() => setOpenTooltips(prev => ({ ...prev, [params.row.id]: false }))}
          >
            <IconButton
              onClick={() => {
                setOpenTooltips(prev => ({ ...prev, [params.row.id]: false })) // Força o fechamento do tooltip ao clicar
                handleView(params.row)
              }}
              size="small"
              aria-label={translate('Visualizar')}
              disabled={loadingRowId === params.row.id}
            >
              {loadingRowId === params.row.id ? (
                <CircularProgress size={20} />
              ) : (
                <VisibilityIcon />
              )}
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ]

  const clearFilters = () => {
    setStartDate(null)
    setFinishDate(null)
    initialPeriod.set('')
    userId.set(null)
    action.set(null)
    setRows([])
    setSortModel([{ field: 'created_at', sort: 'desc' }])
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
    expandDeviceReports.set(false)
  }

  const submit = async (
    model = paginationModel,
    currentSortModel: GridSortModel = sortModel
  ) => {
    if (!validateSearchParams(startDate, finishDate, translate)) {
      return false
    }

    // Após validação, startDate e finishDate são garantidamente strings
    const start: string = startDate as string
    const finish: string = finishDate as string

    loadingButton.set(true)

    const page = model.page
    const pageSize = model.pageSize

    const orders = currentSortModel.map(({ field, sort }) => ({
      field,
      sort: sort || 'desc',
    }))

    try {
      const searchLogData = await fetchDeviceReportLogs({
        user_id: userId.get() || undefined,
        start_date: start,
        finish_date: finish,
        action: action.get(),
        origin: originType.get(),
        device_id: selectedDevice.get(),
        station_id: selectedStations.get(),
        page: page,
        limit: pageSize,
        orders:
          orders.length > 0 ? orders : [{ field: 'created_at', sort: 'desc' }],
      })

      const mappedRows = mapSearchLogDataToRows(
        searchLogData.data,
        page,
        pageSize,
        translate,
        getReportNameFromRoute,
        extractDatesFromContent
      )

      setRows(mappedRows)
      setRowCount(searchLogData.rowsCount)
    } finally {
      loadingButton.set(false)
      expandDeviceReports.set(true)
    }
  }

  const searchFromFirstPage = () => {
    const firstPageModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    submit(firstPageModel, sortModel)
  }

  const handleView = async (row: DeviceReportSearchLogRow) => {
    setLoadingRowId(row.id ?? null)
    try {
      if (row.id == null) {
        notify({
          type: 'error',
          message: translate('Invalid log id'),
        })
        return
      }

      const details = await getDeviceLogDetails(Number(row.id))
      setSelectedRow(details)
      setOpenModal(true)
    } catch (error) {
      notify({
        type: 'error',
        message: translate('Erro ao carregar detalhes do log'),
      })
    } finally {
      setLoadingRowId(null)
    }
  }

  const modalFields = React.useMemo(() => getModalFields(selectedRow, translate), [selectedRow, translate])

  return (
    <>
      <InternalContent
        title={`${translate('Filters')}`}
        sxFont={{ fontWeight: 'bold', fontSize: 18 }}
      >
        <AuditDeviceReportFilters
          hidden={expandFilterSearch.get()}
          loadingButton={loadingButton}
          labelButton={labelButton}
          focusChange={focusChange}
          resetValues={resetValues}
          userId={userId}
          initialPeriod={initialPeriod}
          action={action}
          onPeriodChange={(value) => {
            labelButton.set(translate('Search'))
            initialPeriod.set(value)
          }}
          onStartDateChange={(value) => {
            labelButton.set(translate('Search'))
            setStartDate(value)
          }}
          onFinishDateChange={(value) => {
            labelButton.set(translate('Search'))
            setFinishDate(value)
          }}
          clearFilters={() => clearFilters()}
          searchButton={() => searchFromFirstPage()}
          originType={originType}
          selectedDevice={selectedDevice}
          selectedStations={selectedStations}
        />
      </InternalContent>
      <Card sx={{ boxShadow: 4, marginBottom: 2 }}>
        <CardHeader
          title={translate('Actions Performed')}
          avatar={
            <IconButton
              onClick={() =>
                expandDeviceReports.set(!expandDeviceReports.get())
              }
              sx={{
                display: 'flex',
                justifyContent: 'end',
                marginRight: 1,
                padding: 0,
              }}
            >
              {expandDeviceReports.value ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )}
            </IconButton>
          }
          titleTypographyProps={{ fontWeight: 'bold', fontSize: 18 }}
        />
        <Collapse in={expandDeviceReports.value}>
          <DataGrid
            localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            loading={loadingButton.value}
            paginationMode={tableMode}
            paginationModel={paginationModel}
            onPaginationModelChange={(model) => {
              setPaginationModel(model)
              submit(model, sortModel)
            }}
            sortingMode={tableMode}
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              const firstPageModel = {
                page: 0,
                pageSize: paginationModel.pageSize,
              }
              setPaginationModel(firstPageModel)
              submit(firstPageModel, model)
            }}
            columnVisibilityModel={{
              id: false,
            }}
            disableColumnFilter
            disableColumnSelector
            autoHeight
            pagination
            filterMode={tableMode}
            rowCount={rowCount}
            getRowId={(row) => Number(row.id)}
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
              minHeight: '250px',
              margin: 4,
              [`.${gridClasses.columnHeaderTitle}`]: {
                fontWeight: 'bold !important',
              },
            }}
          />
        </Collapse>
      </Card>
      <AuditLogDetailsModal
        open={openModal}
        onClose={clickOutModalClose}
        selectedRow={selectedRow}
        fieldsToDisplay={modalFields}
      />
    </>
  )
}
